/**
 * Scanned Book Service
 * 
 * Handles scanned book CRUD, page extraction, and Turn Color integration.
 * STP (Straight-Through Processing) - no admin review required.
 * 
 * For modular imports, use: import { ... } from '@/services/scan';
 */

import { supabase } from '@/lib/supabase/client';
import { Sentry } from '@/lib/sentry';
// Import shared modules
import { fetchWithRetry } from './scan/retry';
import { BOUNDS_CONFIG, OCR_CONFIG, IMAGE_CONFIG } from '@/lib/scan-config';
import { validateCapture, enhanceImage } from './scan/image-enhancement';
import { lookupTurnCodes } from './scan/turn-color-integration';
import { getBookPages, getBookPage, deleteBookPage, extractPageText, processPageOCR } from './scan/page-extraction';

// Re-export types for backwards compatibility
export type {
  ScannedBook,
  ScannedBookPage,
  BookMetadata,
  TextBlock,
  LayoutData,
  RenderingHints,
  PageBounds,
  IllustrationBoundsResult,
  CaptureValidationResult,
  TurnColorWord,
  PageWithTurnColor,
  EnhanceMode,
  EnhanceImageOptions,
  // Digital pages types
  DigitalPage,
  SceneContext,
  TextPosition,
  EnhancedExtractionResult,
} from './scan/types';

// Import types for internal use
import type {
  ScannedBook,
  ScannedBookPage,
  BookMetadata,
  TextBlock,
  LayoutData,
  RenderingHints,
  PageBounds,
  CaptureValidationResult,
  TurnColorWord,
  PageWithTurnColor,
  // Digital pages types
  DigitalPage,
  SceneContext,
  TextPosition,
  EnhancedExtractionResult,
} from './scan/types';

// ============================================================================
// Book Management (Shared Library Model)
// ============================================================================

/**
 * Find existing book by ISBN GLOBALLY (across all parents).
 * Only returns books with quality_status = 'ready'.
 * Used to enable shared content library.
 */
export async function findBookByIsbnGlobal(isbn: string): Promise<ScannedBook | null> {
  const { data, error } = await supabase
    .from('scanned_books')
    .select('id, parent_id, contributor_id, isbn, title, author, cover_image_url, publisher, total_pages, scan_completed, phase, enabled_colors, quality_status, quality_issues, reconstructed_text, reconstruction_metadata, digital_page_content, request_id, binding, work_group, created_at, updated_at')
    .eq('isbn', isbn)
    .eq('quality_status', 'ready')
    .is('deleted_at', null)
    .order('created_at', { ascending: true })
    .limit(1);

  if (error) {
    console.error('Error finding book by ISBN globally:', error);
    Sentry.captureException(error instanceof Error ? error : new Error(String(error)), {
      tags: { flow: 'scan_pipeline', step: 'isbn_lookup' },
    });
    return null;
  }

  return data?.[0] || null;
}

/**
 * Find existing book by ISBN for a parent (legacy, checks their library)
 * Returns the book if in parent's library, null otherwise
 */
export async function findBookByIsbn(
  parentId: string,
  isbn: string
): Promise<ScannedBook | null> {
  // Check if parent has this ISBN in their library
  const { data, error } = await supabase
    .from('parent_book_library')
    .select(`
      book_id,
      scanned_books!inner (id, parent_id, contributor_id, isbn, title, author, cover_image_url, publisher, total_pages, scan_completed, phase, enabled_colors, quality_status, quality_issues, reconstructed_text, reconstruction_metadata, digital_page_content, request_id, binding, work_group, created_at, updated_at)
    `)
    .eq('parent_id', parentId)
    .eq('scanned_books.isbn', isbn)
    .limit(1);

  if (error) {
    console.error('Error finding book by ISBN:', error);
    Sentry.captureException(error instanceof Error ? error : new Error(String(error)), {
      tags: { flow: 'scan_pipeline', step: 'isbn_lookup' },
    });
    return null;
  }

  // Extract the book from the join
  const result = data?.[0];
  if (result && 'scanned_books' in result) {
    return result.scanned_books as unknown as ScannedBook;
  }

  return null;
}

/**
 * Add a book to a parent's library.
 * Creates a junction table entry.
 */
export async function addBookToLibrary(
  parentId: string,
  bookId: string
): Promise<void> {
  const { error } = await supabase
    .from('parent_book_library')
    .upsert(
      { parent_id: parentId, book_id: bookId },
      { onConflict: 'parent_id,book_id' }
    );

  if (error) {
    console.error('Error adding book to library:', error);
    Sentry.captureException(error instanceof Error ? error : new Error(String(error)), {
      tags: { flow: 'scan_pipeline', step: 'add_to_library' },
    });
    throw new Error(`Failed to add book to library: ${error.message}`);
  }

  console.log(`[ScannedBookService] Added book ${bookId} to parent ${parentId} library`);
}

/**
 * Remove a book from a parent's library (soft delete).
 * Does NOT delete the book from the global catalog - only removes from this parent's library.
 */
export async function removeBookFromLibrary(
  parentId: string,
  bookId: string
): Promise<void> {
  const { error } = await supabase
    .from('parent_book_library')
    .delete()
    .eq('parent_id', parentId)
    .eq('book_id', bookId);

  if (error) {
    throw new Error(`Failed to remove book from library: ${error.message}`);
  }

  console.log(`[ScannedBookService] Removed book ${bookId} from parent ${parentId} library`);
}

/**
 * Create a new scanned book and add to parent's library.
 * Sets contributor_id to track who originally scanned the book.
 */
export async function createScannedBook(
  parentId: string,
  title: string,
  metadata?: Partial<BookMetadata>
): Promise<ScannedBook> {
  const { data, error } = await supabase
    .from('scanned_books')
    .insert({
      parent_id: parentId, // Legacy field, kept for compatibility
      contributor_id: parentId, // New field: who contributed this book
      title,
      isbn: metadata?.isbn || null,
      author: metadata?.author || null,
      cover_image_url: metadata?.coverImageUrl || null,
      publisher: metadata?.publisher || null,
      total_pages: metadata?.pageCount || null,
    })
    .select()
    .single();

  if (error) {
    throw new Error(`Failed to create book: ${error.message}`);
  }

  // Add to parent's library
  await addBookToLibrary(parentId, data.id);

  return data;
}

/**
 * Get or create a scanned book by ISBN.
 * 
 * SHARED LIBRARY MODEL:
 * 1. If ISBN provided, check if book exists GLOBALLY
 * 2. If global book exists and is ready, add to this parent's library
 * 3. If not found, create new book (and add to library)
 * 
 * Returns: { book, isExisting: boolean, addedToLibrary: boolean }
 */
export async function getOrCreateScannedBook(
  parentId: string,
  title: string,
  metadata?: Partial<BookMetadata>
): Promise<{ book: ScannedBook; isExisting: boolean; addedToLibrary?: boolean }> {
  // First, check if parent already has this book in their library
  if (metadata?.isbn) {
    const existingInLibrary = await findBookByIsbn(parentId, metadata.isbn);
    if (existingInLibrary) {
      console.log(`[ScannedBookService] Book already in parent's library: ${metadata.isbn}`);
      return { book: existingInLibrary, isExisting: true };
    }

    // Check if book exists globally (scanned by another parent)
    const globalBook = await findBookByIsbnGlobal(metadata.isbn);
    if (globalBook) {
      console.log(`[ScannedBookService] Found global book with ISBN ${metadata.isbn}, adding to library`);
      await addBookToLibrary(parentId, globalBook.id);
      return { book: globalBook, isExisting: true, addedToLibrary: true };
    }
  }

  // Create new book (also adds to library)
  const book = await createScannedBook(parentId, title, metadata);
  console.log(`[ScannedBookService] Created new book: ${title}`);
  return { book, isExisting: false };
}

/**
 * Get all scanned books for a parent from their library.
 * Uses the junction table for the shared library model.
 */
export async function getScannedBooks(parentId: string): Promise<ScannedBook[]> {
  // Query through junction table
  const { data, error } = await supabase
    .from('parent_book_library')
    .select(`
      book_id,
      added_at,
      scanned_books!inner (id, parent_id, contributor_id, isbn, title, author, cover_image_url, publisher, total_pages, scan_completed, phase, enabled_colors, quality_status, quality_issues, reconstructed_text, reconstruction_metadata, digital_page_content, request_id, binding, work_group, created_at, updated_at)
    `)
    .eq('parent_id', parentId)
    .order('added_at', { ascending: false });

  if (error) {
    throw new Error(`Failed to fetch books: ${error.message}`);
  }

  // Extract books from the join
  const books = (data || []).map((entry: any) => {
    if ('scanned_books' in entry) {
      return entry.scanned_books as unknown as ScannedBook;
    }
    return null;
  }).filter((b: any): b is ScannedBook => b !== null);

  return books;
}

/**
 * Get a single scanned book
 */
export async function getScannedBook(bookId: string): Promise<ScannedBook | null> {
  const { data, error } = await supabase
    .from('scanned_books')
    .select('id, parent_id, contributor_id, isbn, title, author, cover_image_url, publisher, total_pages, scan_completed, phase, enabled_colors, quality_status, quality_issues, reconstructed_text, reconstruction_metadata, digital_page_content, request_id, binding, work_group, created_at, updated_at')
    .eq('id', bookId)
    .maybeSingle();

  if (error) {
    throw new Error(`Failed to fetch book: ${error.message}`);
  }

  return data;
}

/**
 * Update scanned book
 */
export async function updateScannedBook(
  bookId: string,
  updates: Partial<Pick<ScannedBook, 'title' | 'author' | 'scan_completed' | 'phase' | 'enabled_colors'>>
): Promise<ScannedBook> {
  const { data, error } = await supabase
    .from('scanned_books')
    .update(updates)
    .eq('id', bookId)
    .select()
    .single();

  if (error) {
    throw new Error(`Failed to update book: ${error.message}`);
  }

  return data;
}

/**
 * PERMANENTLY delete a scanned book from the global catalog.
 * 
 * ADMIN ONLY - This removes the book from all parent libraries and deletes all data.
 * Parents should use removeBookFromLibrary() instead, which only removes from their library.
 * 
 * Cascades to: scanned_book_pages, digital_pages, parent_book_library entries
 */
export async function deleteScannedBook(bookId: string): Promise<void> {
  const { error } = await supabase
    .from('scanned_books')
    .update({ deleted_at: new Date().toISOString() } as any)
    .eq('id', bookId);

  if (error) {
    throw new Error(`Failed to archive book: ${error.message}`);
  }
  
  console.log(`[ScannedBookService] PERMANENTLY deleted book ${bookId} from global catalog`);
}

/**
 * Mark book as scan completed.
 * Also sets first page image as cover if no cover exists.
 */
export async function completeBookScan(bookId: string): Promise<ScannedBook> {
  // Get the book first to check if it has a cover
  const book = await getScannedBook(bookId);
  
  if (book && !book.cover_image_url) {
    // No cover from ISBN - use first page as cover
    try {
      const pages = await getBookPages(bookId);
      if (pages.length > 0 && pages[0].image_url) {
        await supabase
          .from('scanned_books')
          .update({ cover_image_url: pages[0].image_url })
          .eq('id', bookId);
      }
    } catch (err) {
      console.warn('Could not set first page as cover:', err);
    }
  }
  
  return updateScannedBook(bookId, { scan_completed: true });
}

// ============================================================================
// Capture Validation & Image Enhancement (extracted to ./scan/image-enhancement.ts)
// ============================================================================
export { validateCapture, enhanceImage, enhanceIllustration, extractIllustrationBounds, createIllustrationOnlyImage } from './scan/image-enhancement';

// ============================================================================
// ISBN Lookup
// ============================================================================

/**
 * Look up book metadata by ISBN
 */
export async function lookupIsbn(isbn: string): Promise<BookMetadata | null> {
  const { data: session } = await supabase.auth.getSession();
  if (!session.session) {
    throw new Error('Not authenticated');
  }

  const response = await fetch(
    `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/lookup-isbn`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session.session.access_token}`,
      },
      body: JSON.stringify({ isbn }),
    }
  );

  const result = await response.json();

  if (!response.ok) {
    if (response.status === 404) {
      return null; // Book not found
    }
    throw new Error(result.error || 'ISBN lookup failed');
  }

  return result.book;
}

// ============================================================================
// Page Extraction (extracted to ./scan/page-extraction.ts)
// ============================================================================
export { extractPageText, createPlaceholderPage, updatePageWithOCR, processPageOCR, extractPageTextEnhanced, getBookPages, getBookPage, deleteBookPage } from './scan/page-extraction';


// ============================================================================
// Turn Illustration Generation (extracted to ./scan/turn-illustration.ts)
// ============================================================================

export {
  generateTurnIllustration,
  getTurnArtQueue,
  hasPendingTurnArt,
  getTurnArtProgress,
  generateAllTurnIllustrations,
  resumePendingTurnArtGenerations,
  triggerTurnArtGeneration,
  getTurnArtStatus,
  getTurnArtProgressFromDb,
} from './scan/turn-illustration';

// ============================================================================
// Digital Pages (Sentence-Level Pagination)
// ============================================================================

/**
 * Split text into child-friendly sentences.
 * 
 * DESIGN PRINCIPLES:
 * 1. One sentence per screen - children read one thought at a time
 * 2. Target 5-15 words per sentence - manageable cognitive load
 * 3. Preserve exact punctuation - no mangling
 * 4. Handle dialogue correctly - split at sentence boundaries inside quotes
 * 
 * SPLITTING RULES:
 * - Split after . ! ? followed by space or end
 * - Split after . ! ? followed by closing quote (") then space or end
 * - Keep sentences ending with commas intact (thought continues)
 * - Preserve ellipsis (...) as single sentence
 * - Don't split at abbreviations (Mr., Mrs., Dr., etc.)
 */
function splitIntoChildFriendlySentences(text: string): string[] {
  if (!text || text.trim().length === 0) {
    return [];
  }
  
  // Common abbreviations that shouldn't trigger sentence splits
  const ABBREVIATIONS = /\b(?:Mr|Mrs|Ms|Dr|Prof|Sr|Jr|St|Ave|Rd|Blvd|Mt|vs|etc|e\.g|i\.e|a\.m|p\.m|ol)\.$/i;
  
  const sentences: string[] = [];
  let current = '';
  let i = 0;
  
  while (i < text.length) {
    const char = text[i];
    current += char;
    
    // Check for sentence-ending punctuation
    if (char === '.' || char === '!' || char === '?') {
      // Handle ellipsis - don't split on ...
      if (char === '.' && text.slice(i, i + 3) === '...') {
        current += text[i + 1] + text[i + 2];
        i += 3;
        continue;
      }
      
      // Don't split at abbreviations (only for periods)
      if (char === '.' && ABBREVIATIONS.test(current.trim())) {
        i++;
        continue;
      }
      
      // Look ahead to see what follows
      let lookAhead = i + 1;
      
      // Skip closing quotes after punctuation (straight and curly quotes)
      while (lookAhead < text.length && /[""\u201d'\u2019]/.test(text[lookAhead])) {
        current += text[lookAhead];
        lookAhead++;
      }
      
      // If followed by whitespace or end, this is a sentence boundary
      if (lookAhead >= text.length || /\s/.test(text[lookAhead])) {
        const trimmed = current.trim();
        if (trimmed.length > 0) {
          sentences.push(trimmed);
        }
        current = '';
        i = lookAhead;
        
        // Skip whitespace before next sentence
        while (i < text.length && /\s/.test(text[i])) {
          i++;
        }
        continue;
      }
    }
    
    i++;
  }
  
  // Don't forget any remaining text
  const remaining = current.trim();
  if (remaining.length > 0) {
    sentences.push(remaining);
  }
  
  // POST-PROCESS 1: Merge dialogue with attribution
  // "Ram!" he mooed... should be ONE sentence, not two
  // Pattern: short quote ending with !?" followed by lowercase attribution
  const mergedSentences: string[] = [];
  let idx = 0;
  
  while (idx < sentences.length) {
    const curr = sentences[idx];
    const next = sentences[idx + 1];
    
    // Check if current is short dialogue (ends with punctuation + optional quote)
    // Matches: !" or ?" or ." or !" or ?" (straight and curly quotes)
    const isShortDialogue = curr.length < 40 && /[!?.][\u201d"\u2019']?\s*$/.test(curr);
    
    // Next starts with lowercase = likely attribution
    const nextStartsLowercase = next && /^[a-z]/.test(next);
    
    // Attribution patterns: "he said", "she cried", "they mooed"
    const ATTRIBUTION_PATTERN = /^(he|she|they|it|we|said|cried|mooed|yelled|whispered|asked|replied|shouted|exclaimed|muttered|called|screamed|sighed|groaned|laughed|smiled|grinned|frowned)\b/i;
    const isAttribution = next && ATTRIBUTION_PATTERN.test(next);
    
    // Also check for short next sentences that are clearly attributions
    const isShortAttribution = next && next.length < 50 && nextStartsLowercase;
    
    if (isShortDialogue && (isAttribution || (nextStartsLowercase && isShortAttribution))) {
      // Merge: "Ram!" + "he mooed..." = "Ram!" he mooed..."
      mergedSentences.push(curr + ' ' + next);
      idx += 2; // Skip both
    } else {
      mergedSentences.push(curr);
      idx++;
    }
  }
  
  // POST-PROCESS 2: Split any sentences that are still too long (>20 words)
  // This handles run-on sentences and poetry that wasn't properly punctuated
  const MAX_WORDS = 20;
  const finalSentences: string[] = [];
  
  for (const sentence of mergedSentences) {
    const words = sentence.split(/\s+/);
    
    if (words.length <= MAX_WORDS) {
      finalSentences.push(sentence);
      continue;
    }
    
    // Try to find natural break points (commas, semicolons, em-dashes)
    const breakPattern = /([,;—–])\s+/g;
    const parts = sentence.split(breakPattern);
    
    if (parts.length > 1) {
      // Rebuild with punctuation preserved
      let chunk = '';
      for (let j = 0; j < parts.length; j++) {
        const part = parts[j];
        
        // If this is a punctuation mark, add it to current chunk
        if (/^[,;—–]$/.test(part)) {
          chunk += part;
          continue;
        }
        
        const chunkWords = chunk.split(/\s+/).filter(w => w.length > 0);
        const partWords = part.split(/\s+/).filter(w => w.length > 0);
        
        // If adding this part would exceed limit, save current and start new
        if (chunkWords.length > 0 && chunkWords.length + partWords.length > MAX_WORDS) {
          finalSentences.push(chunk.trim());
          chunk = part;
        } else {
          chunk += (chunk.length > 0 ? ' ' : '') + part;
        }
      }
      
      if (chunk.trim().length > 0) {
        finalSentences.push(chunk.trim());
      }
    } else {
      // No natural break points - just include as-is (better than forcing bad splits)
      finalSentences.push(sentence);
    }
  }
  
  return finalSentences;
}

/**
 * Generate digital pages from a processed scanned page.
 * Splits text into sentences and creates one digital page per sentence.
 * 
 * @param sourcePageId - The scanned_book_page ID to generate from
 * @param bookId - The book ID
 * @param startingDigitalPageNumber - The starting number for digital pages
 * @returns Number of digital pages created
 */
// ============================================================================
// Content Quality Filters for Digital Page Generation
// ============================================================================

/**
 * Detect if text is front matter (copyright, dedication, ISBN, etc.)
 * These should NOT become digital pages for children to read.
 */
function isFrontMatter(text: string): boolean {
  const lowerText = text.toLowerCase();
  
  // Copyright and legal patterns
  const legalPatterns = [
    'copyright', '©', 'all rights reserved', 'manufactured in',
    'no part of this book', 'reproduced in any manner',
    'library of congress', 'cataloging-in-publication',
    'isbn', 'printed in', 'published by', 'first edition',
    'harpercollins', 'scholastic', 'random house', 'penguin',
    'little, brown', 'simon & schuster', 'macmillan',
  ];
  
  // Dedication patterns (typically "For [Name]" at start)
  const dedicationPatterns = [
    /^for\s+[a-z]+\s*[&,]\s*[a-z]+/i,  // "For Name & Name"
    /^for\s+[a-z]+\s*[-–—]\s*[a-z]/i,   // "For Name - initials"
    /^to\s+[a-z]+\s*[&,]\s*[a-z]+/i,    // "To Name & Name"
  ];
  
  // ISBN/catalog number patterns
  const catalogPatterns = [
    /\d{3}-\d-\d{2}-\d{5,6}-\d/,  // ISBN format
    /pz\d+\.\w+/i,                 // Library of Congress call number
    /\[\d+\.\s*\w+-fiction\]/i,   // Subject classification
  ];
  
  // Check legal patterns
  for (const pattern of legalPatterns) {
    if (lowerText.includes(pattern)) return true;
  }
  
  // Check dedication patterns
  for (const pattern of dedicationPatterns) {
    if (pattern.test(text)) return true;
  }
  
  // Check catalog patterns
  for (const pattern of catalogPatterns) {
    if (pattern.test(text)) return true;
  }
  
  return false;
}

/**
 * Detect if text is "margin noise" - garbage from OCR picking up 
 * text from the opposite page in a book spread.
 * 
 * Patterns:
 * - Very short fragments on separate lines
 * - Single words/letters separated by newlines
 * - Text that's mostly newlines with short content
 */
function isMarginNoise(text: string): boolean {
  const lines = text.split('\n').map(l => l.trim()).filter(l => l.length > 0);
  
  // If most lines are very short (1-4 chars), it's margin noise
  if (lines.length >= 3) {
    const shortLines = lines.filter(l => l.length <= 4).length;
    if (shortLines / lines.length > 0.5) {
      return true;
    }
  }
  
  // Pattern: starts with short fragments like "If a\nup a\nwan\n"
  const shortFragmentPattern = /^([a-z]{1,4}\s*\n){3,}/i;
  if (shortFragmentPattern.test(text)) {
    return true;
  }
  
  // High newline ratio with low word count = margin noise
  const newlineCount = (text.match(/\n/g) || []).length;
  const wordCount = text.split(/\s+/).filter(w => w.length > 0).length;
  if (newlineCount > 5 && wordCount < 20 && newlineCount / wordCount > 0.5) {
    return true;
  }
  
  return false;
}

/**
 * Check if a sentence is valid for a digital page.
 * Filters out:
 * - Empty/whitespace only
 * - Single characters or very short fragments
 * - Pure numbers or codes
 * - Front matter content
 */
function isValidSentence(sentence: string): boolean {
  const trimmed = sentence.trim();
  
  // Too short (less than 3 real characters)
  if (trimmed.replace(/[^a-zA-Z]/g, '').length < 3) {
    return false;
  }
  
  // Just a number or code
  if (/^[\d\s\-\.]+$/.test(trimmed)) {
    return false;
  }
  
  // Single word that's not a valid sentence (e.g., "II.", "Title.", "[1.")
  if (trimmed.split(/\s+/).length === 1 && trimmed.length < 10) {
    // Allow common single-word sentences in children's books
    const validSingleWords = ['so...', 'and...', 'but...', 'then...', 'wow!', 'yes!', 'no!'];
    if (!validSingleWords.includes(trimmed.toLowerCase())) {
      return false;
    }
  }
  
  // Front matter content
  if (isFrontMatter(trimmed)) {
    return false;
  }
  
  return true;
}

/**
 * Check if an entire source page is front matter and should be skipped entirely.
 */
function isSourcePageFrontMatter(extractedText: string): boolean {
  if (!extractedText) return false;
  
  const text = extractedText.toLowerCase();
  
  // Strong indicators that this is a copyright/front matter page
  const strongIndicators = [
    'library of congress',
    'cataloging-in-publication',
    'all rights reserved',
    'no part of this book may be',
    'isbn',
  ];
  
  for (const indicator of strongIndicators) {
    if (text.includes(indicator)) {
      console.log(`[DigitalPages] Skipping front matter page (matched: ${indicator})`);
      return true;
    }
  }
  
  return false;
}

export async function generateDigitalPages(
  sourcePageId: string,
  bookId: string,
  startingDigitalPageNumber: number
): Promise<number> {
  console.log(`[DigitalPages] Generating for source page ${sourcePageId}, starting at ${startingDigitalPageNumber}`);
  
  // Fetch source page with extraction data
  const { data: sourcePage, error: fetchError } = await supabase
    .from('scanned_book_pages')
    .select('id, book_id, page_number, image_url, enhanced_image_url, extracted_text, extraction_confidence, page_type, has_illustration, illustration_description, layout_data, rendering_hints, text_blocks, quality_issues, needs_rescan, rescan_reason, detected_page_number, processing_status, created_at, is_superseded, capture_quality')
    .eq('id', sourcePageId)
    .maybeSingle();
  
  if (fetchError || !sourcePage) {
    console.error('[DigitalPages] Failed to fetch source page:', fetchError);
    Sentry.captureException(fetchError instanceof Error ? fetchError : new Error(String(fetchError)), {
      tags: { flow: 'scan_pipeline', step: 'digital_pages' },
    });
    return 0;
  }

  // v7: Check page_type from GPT-4o classification (stored in DB)
  // IMPORTANT: Don't skip based on page_type alone!
  // In v7, extracted_text contains ONLY the kept story segments.
  // A front_matter page might still have story content (e.g., opening line on title page).
  // Only skip if page_type is non-story AND there's no meaningful extracted text.
  const pageType = sourcePage.page_type as string | null;
  const extractedText = sourcePage.extracted_text?.trim() || '';
  const hasStoryContent = extractedText.length > 10; // More than just punctuation
  
  if (pageType === 'illustration_only') {
    console.log(`[DigitalPages] Skipping source page ${sourcePageId} (page_type: illustration_only)`);
    return 0;
  }
  
  // ALWAYS skip back_matter - game boards, author bios, etc. are never story content
  if (pageType === 'back_matter') {
    console.log(`[DigitalPages] Skipping source page ${sourcePageId} (page_type: back_matter)`);
    return 0;
  }
  
  // For front_matter: only skip if no story content was kept by GPT-4o
  // Front matter CAN have story (e.g., "If you give a mouse a cookie" on title page)
  if (pageType === 'front_matter' && !hasStoryContent) {
    console.log(`[DigitalPages] Skipping source page ${sourcePageId} (page_type: front_matter, no story content)`);
    return 0;
  }
  
  // If front_matter has story content, log and continue processing
  if (pageType === 'front_matter' && hasStoryContent) {
    console.log(`[DigitalPages] Processing front_matter page ${sourcePageId} (has ${extractedText.length} chars of story content)`);
  }
  
  // FILTER 1: Skip entire source pages that are front matter (fallback regex detection)
  if (isSourcePageFrontMatter(sourcePage.extracted_text || '')) {
    console.log(`[DigitalPages] Skipping source page ${sourcePageId} (front matter detected)`);
    return 0;
  }
  
  // FILTER 2: Skip pages with margin noise (garbage OCR from spread capture)
  if (isMarginNoise(sourcePage.extracted_text || '')) {
    console.log(`[DigitalPages] Skipping source page ${sourcePageId} (margin noise detected)`);
    return 0;
  }
  
  // Get sentences - either from the stored array or split from extracted_text
  // Note: 'sentences' column may not exist yet in DB types, so cast to any
  let sentences: string[] = (sourcePage as any).sentences || [];
  
  // Fallback: if no sentences stored, split the extracted text
  if (sentences.length === 0 && sourcePage.extracted_text) {
    sentences = splitIntoChildFriendlySentences(sourcePage.extracted_text);
  }
  
  // FILTER 3: Validate each sentence for quality
  const originalCount = sentences.length;
  sentences = sentences
    .map(s => s.trim())
    .filter(s => s.length > 0)
    .filter(isValidSentence);
  
  if (originalCount > 0 && sentences.length < originalCount) {
    console.log(`[DigitalPages] Filtered ${originalCount - sentences.length}/${originalCount} low-quality sentences`);
  }
  
  if (sentences.length === 0) {
    console.log('[DigitalPages] No valid sentences to generate digital pages from');
    return 0;
  }
  
  // Build digital pages to insert
  // Note: 'scene_context' column may not exist yet in DB types, so cast to any
  const digitalPages = sentences.map((sentence, index) => ({
    book_id: bookId,
    source_page_id: sourcePageId,
    sentence_index: index,
    digital_page_number: startingDigitalPageNumber + index,
    sentence_text: sentence,
    turn_art_url: sourcePage.enhanced_image_url || null,
    text_position: { position: 'bottom' }, // Default, will be updated by Turn Art
    scene_context: (sourcePage as any).scene_context || null,
  }));
  
  // Insert digital pages
  // Note: Using 'as any' because digital_pages table types aren't generated yet
  const { data: inserted, error: insertError } = await (supabase as any)
    .from('digital_pages')
    .insert(digitalPages)
    .select('id');
  
  if (insertError) {
    console.error('[DigitalPages] Failed to insert:', insertError);
    Sentry.captureException(insertError instanceof Error ? insertError : new Error(String(insertError)), {
      tags: { flow: 'scan_pipeline', step: 'digital_pages' },
    });
    return 0;
  }

  console.log(`[DigitalPages] Created ${inserted?.length || 0} digital pages`);
  return inserted?.length || 0;
}

/**
 * Generate digital pages for all pages in a book.
 * Processes pages in order to maintain correct digital page numbering.
 * 
 * @param bookId - The book ID
 * @returns Total number of digital pages created
 */
export async function generateAllDigitalPages(bookId: string): Promise<number> {
  console.log(`[DigitalPages] Generating all digital pages for book ${bookId}`);
  
  // Delete existing digital pages first (regeneration)
  // Note: Using 'as any' because digital_pages table types aren't generated yet
  await (supabase as any)
    .from('digital_pages')
    .delete()
    .eq('book_id', bookId);
  
  // Get all non-superseded pages in order
  const { data: pages, error: fetchError } = await supabase
    .from('scanned_book_pages')
    .select('id, page_number')
    .eq('book_id', bookId)
    .eq('is_superseded', false)
    .order('page_number', { ascending: true });
  
  if (fetchError || !pages) {
    console.error('[DigitalPages] Failed to fetch pages:', fetchError);
    Sentry.captureException(fetchError instanceof Error ? fetchError : new Error(String(fetchError)), {
      tags: { flow: 'scan_pipeline', step: 'digital_pages' },
    });
    return 0;
  }

  let totalCreated = 0;
  let currentDigitalPageNumber = 1;
  
  for (const page of pages) {
    const created = await generateDigitalPages(
      page.id,
      bookId,
      currentDigitalPageNumber
    );
    totalCreated += created;
    currentDigitalPageNumber += created;
  }
  
  console.log(`[DigitalPages] Total created: ${totalCreated}`);
  return totalCreated;
}

// ============================================================================
// v9 Book-Level Reconstruction
// ============================================================================

interface ReconstructionResult {
  success: boolean;
  reconstructedText?: string;
  sentences?: string[];
  sentenceCount?: number;
  // v18: Quality gate fields
  qualityStatus?: 'ready' | 'needs_review' | 'failed';
  qualityIssues?: string[];
  metadata?: {
    version: string;
    coverage: {
      initial: number;
      final: number;
    };
    qualityStatus: string;
    qualityIssues: string[];
  };
  verification?: {
    isGrounded: boolean;
    coverage: number;
    hallucinations: string[];
    hallucinationCount: number;
  };
  error?: string;
}

/**
 * Call the reconstruct-book-text edge function for book-level reconstruction.
 * This is the v9 architecture - GPT-4o reconstructs the entire book at once.
 */
async function callReconstructBookText(bookId: string): Promise<ReconstructionResult> {
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
  const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
  
  console.log(`[v9] Calling reconstruct-book-text for book ${bookId}`);
  
  try {
    const response = await fetch(`${supabaseUrl}/functions/v1/reconstruct-book-text`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${anonKey}`,
        'apikey': anonKey,
      },
      body: JSON.stringify({ book_id: bookId }),
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error(`[v9] Edge function error: ${response.status} - ${errorText}`);
      Sentry.captureException(new Error(`reconstruct-book-text HTTP ${response.status}: ${errorText}`), {
        tags: { flow: 'scan_pipeline', step: 'reconstruct_book_text' },
      });
      return { success: false, error: `HTTP ${response.status}: ${errorText}` };
    }
    
    const result = await response.json();
    return result;
  } catch (error) {
    console.error('[v9] Failed to call reconstruct-book-text:', error);
    Sentry.captureException(error instanceof Error ? error : new Error(String(error)), {
      tags: { flow: 'scan_pipeline', step: 'reconstruct_book_text' },
    });
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error'
    };
  }
}

/**
 * v18: Generate digital pages with QUALITY GATE enforcement.
 * 
 * CRITICAL ARCHITECTURE (v18):
 * - reconstructed_text is the SINGLE SOURCE OF TRUTH
 * - quality_status MUST be 'ready' before generating digital pages
 * - NO FALLBACK to legacy per-page processing - quality gate is enforced
 * - Books that fail quality are marked 'needs_review' and blocked
 * 
 * Flow:
 * 1. Check quality_status from database
 * 2. If 'ready': use stored reconstructed_text
 * 3. If 'needs_review'/'failed': return 0 (blocked)
 * 4. If null/processing: call reconstruction, check quality result
 * 5. Create digital pages ONLY from validated content
 */
export interface DigitalPageGenerationResult {
  count: number;
  status: 'success' | 'needs_review' | 'failed' | 'no_content';
  qualityIssues?: string[];
  coverage?: number;
}

export async function generateAllDigitalPagesV9(bookId: string): Promise<number> {
  const result = await generateDigitalPagesWithQualityGate(bookId);
  return result.count;
}

/**
 * Main quality-gated digital page generation.
 * Returns detailed result for caller to handle appropriately.
 */
export async function generateDigitalPagesWithQualityGate(bookId: string): Promise<DigitalPageGenerationResult> {
  console.log(`[v18] Starting quality-gated digital page generation for book ${bookId}`);
  
  // Delete existing digital pages first
  await (supabase as any)
    .from('digital_pages')
    .delete()
    .eq('book_id', bookId);
  
  // STEP 1: Check quality_status and reconstructed_text
  const { data: book, error: bookError } = await (supabase as any)
    .from('scanned_books')
    .select('reconstructed_text, reconstruction_metadata, quality_status, quality_issues')
    .eq('id', bookId)
    .maybeSingle();
  
  if (bookError) {
    console.error('[v18] Failed to fetch book:', bookError);
    Sentry.captureException(bookError instanceof Error ? bookError : new Error(String(bookError)), {
      tags: { flow: 'scan_pipeline', step: 'digital_pages_quality_gate' },
    });
    return { count: 0, status: 'failed', qualityIssues: ['database_error'] };
  }
  
  let sentences: string[] = [];
  let qualityStatus = book?.quality_status;
  let qualityIssues = book?.quality_issues || [];
  let coverage = book?.reconstruction_metadata?.coverage?.final;
  
  // STEP 2: Check if reconstruction is needed
  if (!book?.reconstructed_text || book.reconstructed_text.trim().length < 50) {
    console.log(`[v18] No stored text, calling reconstruct-book-text...`);
    
    const result = await callReconstructBookText(bookId);
    
    if (!result.success) {
      console.error('[v18] Reconstruction failed:', result.error);
      Sentry.captureException(new Error(String(result.error)), {
        tags: { flow: 'scan_pipeline', step: 'digital_pages_quality_gate' },
      });
      return { count: 0, status: 'failed', qualityIssues: ['reconstruction_failed'] };
    }
    
    // Use the quality status from reconstruction
    qualityStatus = result.qualityStatus;
    qualityIssues = result.qualityIssues || [];
    sentences = result.sentences || [];
    coverage = result.metadata?.coverage?.final;
    
    console.log(`[v18] Reconstruction returned: status=${qualityStatus}, sentences=${sentences.length}`);
  } else {
    // Use stored reconstructed text
    sentences = book.reconstructed_text
      .split('\n')
      .map((s: string) => s.trim())
      .filter((s: string) => s.length > 10);
    
    console.log(`[v18] Using stored reconstructed_text (${sentences.length} sentences, status=${qualityStatus})`);
  }
  
  // STEP 3: QUALITY GATE - Block if quality is not 'ready'
  // v18: NO FALLBACK - if quality is bad, book is not ready for reading
  if (qualityStatus === 'needs_review') {
    console.warn(`[v18] QUALITY GATE BLOCKED: Book needs review. Issues: [${qualityIssues.join(', ')}]`);
    return { 
      count: 0, 
      status: 'needs_review', 
      qualityIssues,
      coverage
    };
  }
  
  if (qualityStatus === 'failed' || sentences.length === 0) {
    console.error(`[v18] QUALITY GATE BLOCKED: Reconstruction failed or no sentences`);
    Sentry.captureException(new Error('QUALITY GATE BLOCKED: Reconstruction failed or no sentences'), {
      tags: { flow: 'scan_pipeline', step: 'digital_pages_quality_gate' },
    });
    return {
      count: 0,
      status: 'failed',
      qualityIssues: qualityIssues.length > 0 ? qualityIssues : ['no_sentences'],
      coverage
    };
  }
  
  // Additional sanity check even if status is 'ready'
  if (sentences.length < 5) {
    console.warn(`[v18] QUALITY GATE BLOCKED: Only ${sentences.length} sentences (min 5 required)`);
    // Update status to needs_review
    await (supabase as any)
      .from('scanned_books')
      .update({ 
        quality_status: 'needs_review',
        quality_issues: [...qualityIssues, `too_few_sentences:${sentences.length}`]
      })
      .eq('id', bookId);
    
    return { 
      count: 0, 
      status: 'needs_review', 
      qualityIssues: [...qualityIssues, `too_few_sentences:${sentences.length}`],
      coverage
    };
  }
  
  console.log(`[v18] Quality gate PASSED: ${sentences.length} sentences, coverage=${coverage}%`);
  
  // STEP 4: Get a source page for Turn Art URL
  const { data: sourcePage } = await supabase
    .from('scanned_book_pages')
    .select('id, enhanced_image_url, image_url')
    .eq('book_id', bookId)
    .eq('is_superseded', false)
    .order('page_number', { ascending: true })
    .limit(1)
    .maybeSingle();
  
  const defaultSourcePageId = sourcePage?.id || null;
  const defaultTurnArtUrl = sourcePage?.enhanced_image_url || sourcePage?.image_url || null;
  
  if (!defaultSourcePageId) {
    console.error('[v18] No source page found');
    Sentry.captureException(new Error('No source page found for digital page generation'), {
      tags: { flow: 'scan_pipeline', step: 'digital_pages_quality_gate' },
    });
    return { count: 0, status: 'failed', qualityIssues: ['no_source_page'] };
  }
  
  // STEP 5: Create digital pages from validated sentences
  const digitalPages = sentences.map((sentence, index) => ({
    book_id: bookId,
    source_page_id: defaultSourcePageId,
    sentence_index: index,
    digital_page_number: index + 1,
    sentence_text: sentence,
    turn_art_url: defaultTurnArtUrl,
    text_position: { position: 'bottom' },
    scene_context: null,
  }));
  
  // Batch insert
  const { data: inserted, error: insertError } = await (supabase as any)
    .from('digital_pages')
    .insert(digitalPages)
    .select('id');
  
  if (insertError) {
    console.error('[v18] Failed to insert digital pages:', insertError);
    Sentry.captureException(insertError instanceof Error ? insertError : new Error(String(insertError)), {
      tags: { flow: 'scan_pipeline', step: 'digital_pages_quality_gate' },
    });
    return { count: 0, status: 'failed', qualityIssues: ['insert_failed'] };
  }
  
  const count = inserted?.length || 0;
  console.log(`[v18] SUCCESS: Created ${count} digital pages from quality-validated text`);
  
  return { 
    count, 
    status: 'success',
    coverage
  };
}


/**
 * Get digital pages for reading a book.
 * Returns pages in order with Turn Color word data.
 * 
 * @param bookId - The book ID
 * @returns Array of digital pages ready for reading
 */
export async function getDigitalPages(bookId: string): Promise<DigitalPage[]> {
  // Note: Using 'as any' because digital_pages table types aren't generated yet
  const { data: pages, error } = await (supabase as any)
    .from('digital_pages')
    .select('id, book_id, source_page_id, sentence_index, digital_page_number, sentence_text, turn_art_url, text_position, scene_context, created_at, updated_at')
    .eq('book_id', bookId)
    .order('digital_page_number', { ascending: true });
  
  if (error || !pages) {
    console.error('[DigitalPages] Failed to fetch:', error);
    Sentry.captureException(error instanceof Error ? error : new Error(String(error)), {
      tags: { flow: 'scan_pipeline', step: 'get_digital_pages' },
    });
    return [];
  }

  // Collect all words from all sentences for Turn Color lookup
  const allWords: string[] = [];
  (pages as any[]).forEach((page: any) => {
    // BUG FIX: Null-safe sentence_text handling
    const sentenceText = page.sentence_text;
    if (!sentenceText || typeof sentenceText !== 'string') {
      console.warn(`[DigitalPages] Page ${page.id} has invalid sentence_text:`, sentenceText);
      return;
    }
    const words = sentenceText
      .split(/\s+/)
      .map((w: string) => w.toLowerCase().replace(/[^a-z'-]/g, ''))
      .filter((w: string) => w.length > 0);
    allWords.push(...words);
  });
  
  // Batch lookup Turn Color codes
  const turnCodes = await lookupTurnCodes(allWords);
  
  // Transform to DigitalPage type with Turn Color data
  return (pages as any[]).map((page: any) => {
    // BUG FIX: Null-safe sentence_text handling
    const sentenceText = page.sentence_text;
    const words = (sentenceText && typeof sentenceText === 'string')
      ? sentenceText
          .split(/\s+/)
          .filter((w: string) => w.length > 0)
          .map((word: string) => {
            const cleanWord = word.toLowerCase().replace(/[^a-z'-]/g, '');
            const lookup = turnCodes.get(cleanWord) || null;
            return {
              word,
              turnCode: lookup?.turnCode || null,
              inLexicon: lookup !== null,
              skillId: lookup?.skillId ?? undefined,
            };
          })
      : [];
    
    return {
      id: page.id,
      bookId: page.book_id,
      sourcePageId: page.source_page_id,
      sentenceIndex: page.sentence_index,
      digitalPageNumber: page.digital_page_number,
      sentenceText: page.sentence_text,
      turnArtUrl: page.turn_art_url,
      textPosition: page.text_position as TextPosition | null,
      sceneContext: page.scene_context as SceneContext | null,
      words,
      createdAt: page.created_at,
      updatedAt: page.updated_at,
    };
  });
}

/**
 * Check if a book has digital pages generated
 */
export async function hasDigitalPages(bookId: string): Promise<boolean> {
  // Note: Using 'as any' because digital_pages table types aren't generated yet
  const { count, error } = await (supabase as any)
    .from('digital_pages')
    .select('id', { count: 'exact', head: true })
    .eq('book_id', bookId);
  
  return !error && (count || 0) > 0;
}

/**
 * Get digital page count for a book
 */
export async function getDigitalPageCount(bookId: string): Promise<number> {
  // Note: Using 'as any' because digital_pages table types aren't generated yet
  const { count, error } = await (supabase as any)
    .from('digital_pages')
    .select('id', { count: 'exact', head: true })
    .eq('book_id', bookId);
  
  return error ? 0 : (count || 0);
}

/**
 * Update Turn Art URL for all digital pages from a source page
 */
export async function updateDigitalPagesTurnArt(
  sourcePageId: string,
  turnArtUrl: string,
  textPosition?: TextPosition
): Promise<void> {
  const updateData: Record<string, unknown> = {
    turn_art_url: turnArtUrl,
  };
  
  if (textPosition) {
    updateData.text_position = textPosition;
  }
  
  // Note: Using 'as any' because digital_pages table types aren't generated yet
  const { error } = await (supabase as any)
    .from('digital_pages')
    .update(updateData)
    .eq('source_page_id', sourcePageId);
  
  if (error) {
    console.error('[DigitalPages] Failed to update Turn Art:', error);
    Sentry.captureException(error instanceof Error ? error : new Error(String(error)), {
      tags: { flow: 'scan_pipeline', step: 'update_digital_pages_turn_art' },
    });
  }
}

// ============================================================================
// Turn Color Integration (delegated to scan/turn-color-integration.ts)
// ============================================================================
export { getPageWithTurnColor, getBookPagesWithTurnColor, getColorMappings } from './scan/turn-color-integration';

// ============================================================================
// Post-Scan Processing (delegated to scan/consolidation.ts + scan/background-processing.ts)
// ============================================================================
export {
  runPostScanCleanup,
  consolidateBookPages,
  type PostScanCleanupResult,
  type ConsolidationResult,
} from './scan/consolidation';

export {
  saveCapturesToStorage,
  triggerBackgroundProcessing,
  getPagesNeedingRescan,
  getBookProcessingStatus,
  type CaptureForStorage,
  type SaveCapturesResult,
} from './scan/background-processing';

// NOTE: The code below was extracted to scan/consolidation.ts and scan/background-processing.ts in v8.74.
// The rest of this file ends here. See scan/ modules for the implementations.
// REMOVED_EXTRACTION_MARKER
